import { DataLayer, EventData } from 'features/systemLens'
import {
  AXIS_DOMAIN_OFFSETS,
  AXIS_TICK_FONT,
  AXIS_TITLE_FONT,
  CHART_COLORS,
  CHART_MARGINS,
  DEFAULT_CHART_SETTINGS,
  resolveChartColor,
  X_AXIS_DEFAULTS,
  Y_AXIS_DEFAULTS,
} from 'features/timeSeries/config/constants'
import { getSensorColor } from 'features/timeSeries/config/timeSeriesColors'
import { Layout, LayoutAxis } from 'plotly.js'
import {
  EnhancedSensorEntry,
  RegressionConfig,
  SensorAxisMapping,
  TimeSeriesDerivedValueEntryJSON,
} from 'types'

import { hasFiniteRange, SensorRangeMap } from './axisRangeHelpers'
import {
  generateEventMarkers,
  getThresholdColor,
  getThresholdLabel,
} from './chartHelpers'

interface ThresholdValue {
  type: string
  value: number
  sensors: string[]
}

interface PlotlyLayoutOptions {
  sensorAxisMapping: SensorAxisMapping
  dataLayers: DataLayer[]
  operationalEvents: EventData[]
  singleScaleMode: boolean
  allSensorsToRender: Array<{ id: string; name: string }>
  uniqueThresholds: ThresholdValue[]
  enhancedSensorEntries: EnhancedSensorEntry[]
  customAxisRanges: Record<string, { min?: number; max?: number }>
  sensorRanges: SensorRangeMap
  sensorColors: string[]
  derivedValues: TimeSeriesDerivedValueEntryJSON[]
  regressionConfigs: RegressionConfig[]
  xAxisSensorId?: string
  customXAxisRange?: { min?: number; max?: number }
  unitMapping: Record<string, string>
  customSensorColors?: Record<string, string>
}

function generateSingleScaleYAxisConfig(options: PlotlyLayoutOptions) {
  const { allSensorsToRender, sensorRanges, customAxisRanges, derivedValues } =
    options

  const yAxesConfig: Record<
    string,
    Partial<LayoutAxis | { autoshift: boolean }>
  > = {}
  // Single scale mode - all sensors share one y-axis
  let overallMin = Infinity
  let overallMax = -Infinity
  const units = new Set<string>()

  // Calculate overall min/max and collect units
  allSensorsToRender.forEach(({ id: sensorId }) => {
    const data = sensorRanges[sensorId]
    if (hasFiniteRange(data)) {
      overallMin = Math.min(overallMin, data.min)
      overallMax = Math.max(overallMax, data.max)

      if (sensorId.startsWith('derived_')) {
        const derivedValue = derivedValues.find((dv) => dv.id === sensorId)
        if (derivedValue?.data.unit && derivedValue.data.unit !== 'No Unit') {
          units.add(derivedValue.data.unit)
        }
      } else if (data.unit !== 'No Unit') {
        units.add(data.unit)
      }
    }
  })

  const hasValidRange = isFinite(overallMin) && isFinite(overallMax)
  const buffer = hasValidRange ? (overallMax - overallMin) * 0.1 : 0

  const singleScaleCustomRange = customAxisRanges['single-scale']
  const hasCustomRange =
    singleScaleCustomRange &&
    (singleScaleCustomRange.min !== undefined ||
      singleScaleCustomRange.max !== undefined)

  let range: [number, number] | undefined
  let tickValues: number[] | undefined

  if (hasCustomRange) {
    const minVal =
      singleScaleCustomRange.min ?? (hasValidRange ? overallMin - buffer : 0)
    const maxVal =
      singleScaleCustomRange.max ?? (hasValidRange ? overallMax + buffer : 1)
    range = [minVal, maxVal]
    tickValues = [minVal, maxVal]
  } else if (hasValidRange) {
    tickValues = [overallMin, overallMax]
    range = [overallMin - buffer, overallMax + buffer]
  }

  const unitText =
    units.size === 0
      ? ''
      : units.size === 1
        ? ` (${Array.from(units)[0]})`
        : ` (${Array.from(units).join(', ')})`

  yAxesConfig.yaxis = {
    ...Y_AXIS_DEFAULTS,
    title: {
      text: `Value${unitText}`,
      font: AXIS_TITLE_FONT,
    },
    tickfont: AXIS_TICK_FONT,
    tickmode: range ? 'array' : 'auto',
    tickvals: tickValues,
    ticktext: tickValues?.map((v: number) => v.toPrecision(4)),
    autorange: !range,
    range: range,
    side: 'left',
  }
  return yAxesConfig
}

function generateMultiScaleYAxisConfig(options: PlotlyLayoutOptions) {
  const {
    allSensorsToRender,
    sensorRanges,
    sensorAxisMapping,
    customAxisRanges,
    derivedValues,
    enhancedSensorEntries,
    customSensorColors,
  } = options

  const yAxesConfig: Record<
    string,
    Partial<LayoutAxis | { autoshift: boolean }>
  > = {}

  // Multi scale mode - each sensor gets its own axis
  allSensorsToRender.forEach(({ id: sensorId }, sensorIndex) => {
    const axisInfo = sensorAxisMapping[sensorId]
    if (!axisInfo) return
    const sensorData = sensorRanges[sensorId]

    const isDerived = sensorId.startsWith('derived_')
    let sensorName: string
    let sensorUnit: string | undefined

    if (isDerived) {
      const derivedValue = derivedValues.find((dv) => dv.id === sensorId)
      sensorName = derivedValue?.data.name || sensorId
      sensorUnit = derivedValue?.data.unit || 'No Unit'
    } else {
      const sensor = enhancedSensorEntries.find((e) => e.id === sensorId)
      sensorName = (sensor?.data?.name as string) || sensorId
      sensorUnit = sensorData?.unit
    }

    const axisColor =
      customSensorColors?.[sensorId] || getSensorColor(sensorIndex)
    const plotlyAxisKey =
      axisInfo.axis === 'y' ? 'yaxis' : `yaxis${axisInfo.axis.substring(1)}`

    const customRange = customAxisRanges[sensorId]
    const hasCustomRange =
      customRange &&
      (customRange.min !== undefined || customRange.max !== undefined)

    const hasValidRange = hasFiniteRange(sensorData)
    const buffer = hasValidRange ? (sensorData.max - sensorData.min) * 0.1 : 0

    let range: [number, number] | undefined
    let tickValues: number[] | undefined

    if (hasCustomRange) {
      const minVal =
        customRange.min ?? (hasValidRange ? sensorData.min - buffer : 0)
      const maxVal =
        customRange.max ?? (hasValidRange ? sensorData.max + buffer : 1)
      range = [minVal, maxVal]
      tickValues = [minVal, maxVal]
    } else if (hasValidRange) {
      tickValues = [sensorData.min, sensorData.max]
      range = [sensorData.min - buffer, sensorData.max + buffer]
    }

    yAxesConfig[plotlyAxisKey] = {
      ...Y_AXIS_DEFAULTS,
      title: {
        text: `${sensorName}${sensorUnit && sensorUnit !== 'No Unit' ? ` (${sensorUnit})` : ''}`,
        font: {
          ...AXIS_TITLE_FONT,
          color: axisColor,
        },
      },
      tickfont: {
        ...AXIS_TICK_FONT,
        color: axisColor,
      },
      tickmode: range ? 'array' : 'auto',
      tickvals: tickValues,
      ticktext: tickValues?.map((v: number) => v.toPrecision(4)),
      autorange: !range,
      range: range,
      autoshift: true,
      side: axisInfo.side,
      anchor: sensorIndex === 0 ? 'x' : 'free',
      overlaying: sensorIndex > 0 ? 'y' : undefined,
    }
  })
  return yAxesConfig
}

/**
 * Generate Y-axis configuration for Plotly layout
 */
export const generateYAxesConfig = (options: PlotlyLayoutOptions) => {
  if (options.singleScaleMode) {
    return generateSingleScaleYAxisConfig(options)
  } else {
    return generateMultiScaleYAxisConfig(options)
  }
}

/**
 * Generate X-axis configuration for Plotly layout
 */
export const generateXAxisConfig = (options: PlotlyLayoutOptions) => {
  const {
    xAxisSensorId,
    derivedValues,
    enhancedSensorEntries,
    unitMapping,
    customXAxisRange,
    regressionConfigs,
    sensorAxisMapping,
  } = options

  const hasMultipleLeftAxes =
    Object.values(sensorAxisMapping).filter((m) => m.side === 'left').length > 1
  const hasMultipleRightAxes =
    Object.values(sensorAxisMapping).filter((m) => m.side === 'right').length >
    1

  if (xAxisSensorId) {
    let xAxisTitle
    let xAxisUnit = ''

    if (xAxisSensorId.startsWith('derived_')) {
      const derivedValue = derivedValues.find((dv) => dv.id === xAxisSensorId)
      xAxisTitle = derivedValue?.data.name || xAxisSensorId
      xAxisUnit = derivedValue?.data.unit || 'No Unit'
    } else {
      const sensor = enhancedSensorEntries.find((e) => e.id === xAxisSensorId)
      xAxisTitle = (sensor?.data?.name as string) || xAxisSensorId
      const unit = unitMapping[xAxisTitle]
      xAxisUnit = unit && unit !== 'No Unit' ? ` (${unit})` : ''
    }

    return {
      ...X_AXIS_DEFAULTS,
      linecolor: resolveChartColor(CHART_COLORS.GRID_LINE),
      type: 'linear' as const,
      title: {
        text: `${xAxisTitle}${xAxisUnit}`,
        font: AXIS_TITLE_FONT,
      },
      tickfont: AXIS_TITLE_FONT,
      autorange: true,
      visible: true,
      domain:
        hasMultipleLeftAxes || hasMultipleRightAxes
          ? [
              hasMultipleLeftAxes ? AXIS_DOMAIN_OFFSETS.MULTIPLE_LEFT : 0,
              hasMultipleRightAxes ? AXIS_DOMAIN_OFFSETS.MULTIPLE_RIGHT : 1,
            ]
          : undefined,
      ...(customXAxisRange &&
        (customXAxisRange.min !== undefined ||
          customXAxisRange.max !== undefined) && {
          autorange: false,
          range: [customXAxisRange.min, customXAxisRange.max],
        }),
    }
  } else {
    // Time-based x-axis
    return {
      ...X_AXIS_DEFAULTS,
      linecolor: resolveChartColor(CHART_COLORS.GRID_LINE),
      type: 'date' as const,
      tickfont: AXIS_TITLE_FONT,
      nticks: 8,
      visible: true,
      domain:
        hasMultipleLeftAxes || hasMultipleRightAxes
          ? [
              hasMultipleLeftAxes ? AXIS_DOMAIN_OFFSETS.MULTIPLE_LEFT : 0,
              hasMultipleRightAxes ? AXIS_DOMAIN_OFFSETS.MULTIPLE_RIGHT : 1,
            ]
          : undefined,
      ...(customXAxisRange &&
        (customXAxisRange.min !== undefined ||
          customXAxisRange.max !== undefined) && {
          autorange: false,
          range: [
            customXAxisRange.min !== undefined
              ? new Date(customXAxisRange.min).toISOString()
              : undefined,
            customXAxisRange.max !== undefined
              ? new Date(customXAxisRange.max).toISOString()
              : undefined,
          ],
        }),
      ...(!customXAxisRange?.max &&
        regressionConfigs.some(
          (config) => config.enabled && config.endDate
        ) && {
          autorange: true,
        }),
    }
  }
}

/**
 * Generate the complete Plotly layout configuration
 */
export const generatePlotlyLayout = (
  options: PlotlyLayoutOptions
): Partial<Layout> => {
  const {
    sensorAxisMapping,
    dataLayers,
    operationalEvents,
    uniqueThresholds,
    enhancedSensorEntries,
    allSensorsToRender,
  } = options

  const shapes: Partial<Layout>['shapes'] = []
  const annotations: Partial<Layout>['annotations'] = []

  // Calculate if we have multiple axes on each side
  const leftAxesCount = Object.values(sensorAxisMapping).filter(
    (m) => m.side === 'left'
  ).length
  const rightAxesCount = Object.values(sensorAxisMapping).filter(
    (m) => m.side === 'right'
  ).length
  const hasMultipleLeftAxes = leftAxesCount > 1 && !options.singleScaleMode
  const hasMultipleRightAxes = rightAxesCount > 1 && !options.singleScaleMode

  // Add threshold shapes and annotations
  if (dataLayers.find((l) => l.id === 'thresholds')?.enabled) {
    uniqueThresholds.forEach((threshold) => {
      const firstSensor = threshold.sensors[0]
      const sensor = enhancedSensorEntries.find((e) =>
        e.id.includes(firstSensor)
      )
      const sensorId = sensor?.id || firstSensor
      const axisInfo = sensorAxisMapping[sensorId]
      const yref: 'y' | 'y2' | 'y3' | 'y4' = (axisInfo?.axis || 'y') as
        | 'y'
        | 'y2'
        | 'y3'
        | 'y4'

      shapes.push({
        type: 'line',
        x0: hasMultipleLeftAxes ? AXIS_DOMAIN_OFFSETS.MULTIPLE_LEFT : 0,
        x1: hasMultipleRightAxes ? AXIS_DOMAIN_OFFSETS.MULTIPLE_RIGHT : 1,
        xref: 'paper',
        y0: threshold.value,
        y1: threshold.value,
        yref: yref,
        line: {
          color: getThresholdColor(threshold.type),
          width: 2,
          dash: 'dash',
        },
      })

      annotations.push({
        x: axisInfo?.side === 'right' ? 0.9 : 0.1,
        xref: 'paper',
        y: threshold.value,
        yref: yref,
        text: `${getThresholdLabel(threshold.type)} (${threshold.sensors.join(', ')})`,
        showarrow: false,
        font: {
          size: DEFAULT_CHART_SETTINGS.TICK_FONT_SIZE,
          color: getThresholdColor(threshold.type),
        },
        xanchor: axisInfo?.side === 'right' ? 'right' : 'left',
        yanchor: threshold.type.includes('upper') ? 'bottom' : 'top',
      })
    })
  }

  // Generate event markers
  const eventMarkers = generateEventMarkers(
    operationalEvents,
    dataLayers.find((l) => l.id === 'events')?.enabled || false
  )
  shapes.push(...eventMarkers.shapes)
  annotations.push(...eventMarkers.annotations)

  const yAxesConfig = generateYAxesConfig(options)
  const xAxisConfig = generateXAxisConfig(options)
  const hasAnyAxes = allSensorsToRender.length > 0

  return {
    autosize: true,
    paper_bgcolor: resolveChartColor(CHART_COLORS.BACKGROUND),
    plot_bgcolor: resolveChartColor(CHART_COLORS.BACKGROUND),
    font: {
      color: resolveChartColor(CHART_COLORS.TEXT),
      size: DEFAULT_CHART_SETTINGS.AXIS_TITLE_FONT_SIZE,
    },
    margin: {
      l: hasMultipleLeftAxes
        ? CHART_MARGINS.MULTIPLE_LEFT_AXES
        : hasAnyAxes
          ? CHART_MARGINS.SINGLE_AXIS_LEFT
          : CHART_MARGINS.DEFAULT_LEFT,
      r: hasMultipleRightAxes
        ? CHART_MARGINS.MULTIPLE_RIGHT_AXES
        : rightAxesCount > 0
          ? CHART_MARGINS.SINGLE_AXIS_RIGHT
          : CHART_MARGINS.DEFAULT_RIGHT,
      t: CHART_MARGINS.TOP,
      b: CHART_MARGINS.BOTTOM,
    },
    xaxis: xAxisConfig,
    ...yAxesConfig,
    showlegend: false,
    legend: {
      x: 0.5,
      y: 1.05,
      xanchor: 'center',
      yanchor: 'bottom',
      orientation: 'h',
      bgcolor: resolveChartColor(CHART_COLORS.LEGEND_BACKGROUND),
      bordercolor: resolveChartColor(CHART_COLORS.LEGEND_BORDER),
      borderwidth: 1,
      font: {
        size: DEFAULT_CHART_SETTINGS.LEGEND_FONT_SIZE,
      },
      itemsizing: 'constant',
      itemwidth: 30,
    },
    hovermode: 'x unified',
    hoverlabel: {
      bgcolor: resolveChartColor(CHART_COLORS.LEGEND_BACKGROUND),
      bordercolor: resolveChartColor(CHART_COLORS.LEGEND_BORDER),
      font: {
        size: DEFAULT_CHART_SETTINGS.HOVER_FONT_SIZE,
        color: resolveChartColor(CHART_COLORS.TEXT),
        family: DEFAULT_CHART_SETTINGS.FONT_FAMILY,
      },
    },
    shapes,
    annotations,
    dragmode: 'zoom',
    selectdirection: 'h',
    modebar: {
      remove: [
        'zoom2d',
        'pan2d',
        'resetScale2d',
        'autoScale2d',
        'zoomIn2d',
        'zoomOut2d',
      ],
    },
  }
}
